import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, PencilSimple, Trash, LinkSimple, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const empty = { name: '', link1: '', link2: '' };

/**
 * ServiceGroupsPage — Masters → Service Groups.
 * Each group bundles `name + link1 + link2`. Services inherit those URLs at
 * create time. On group edit, if any member service has diverged links the
 * backend returns 409 + a conflict list — we surface a Skip/Overwrite dialog
 * so the operator chooses how to cascade.
 */
export default function ServiceGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState(empty);
  const [conflict, setConflict] = useState(null); // {services: [...]} when 409

  const refresh = () => {
    setLoading(true);
    api.get('/service-groups').then(r => setGroups(r.data || []))
      .catch(() => setGroups([])).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const openCreate = () => { setEditing(null); setForm(empty); setShowDialog(true); };
  const openEdit = (g) => { setEditing(g); setForm({ name: g.name, link1: g.link1 || '', link2: g.link2 || '' }); setShowDialog(true); };

  const save = async (cascade) => {
    const n = form.name.trim();
    if (!n) { toast.error('Group name required'); return; }
    const body = { name: n, link1: form.link1.trim(), link2: form.link2.trim() };
    try {
      if (editing) {
        await api.put(`/service-groups/${editing.id}`, { ...body, cascade: cascade || 'dry_run' });
        toast.success(`Group "${n}" updated`);
      } else {
        await api.post('/service-groups', body);
        toast.success(`Group "${n}" created`);
      }
      setShowDialog(false);
      setConflict(null);
      setForm(empty);
      refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'cascade_conflict') {
        setConflict(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to save');
      }
    }
  };

  const del = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/service-groups/${g.id}`); toast.success('Group deleted'); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      <Card className="rounded-xl border-[#E4E4E7]">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-base font-semibold text-[#0A0A0A]">Service Groups</h1>
              <p className="text-xs text-[#A1A1AA] mt-1">Bundle services by portal — group&apos;s Link 1 / Link 2 auto-fill every member service.</p>
            </div>
            <Button onClick={openCreate} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] rounded-lg h-9 text-xs" data-testid="svc-group-add-btn">
              <Plus size={14} className="mr-1" /> New Group
            </Button>
          </div>

          {loading ? (
            <p className="text-sm text-[#A1A1AA] py-6 text-center">Loading…</p>
          ) : groups.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] italic py-6 text-center">No service groups yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Name</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Link 1</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Link 2</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-right">Services</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-right w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map(g => (
                  <TableRow key={g.id} className="border-[#F4F4F5]" data-testid={`svc-group-row-${g.id}`}>
                    <TableCell className="text-sm font-medium">{g.name}</TableCell>
                    <TableCell className="text-xs max-w-[280px] truncate" title={g.link1 || ''}>
                      {g.link1 ? <a href={g.link1} target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] hover:underline inline-flex items-center gap-1"><LinkSimple size={12} weight="bold" />{g.link1}</a> : <span className="text-[#A1A1AA]">—</span>}
                    </TableCell>
                    <TableCell className="text-xs max-w-[280px] truncate" title={g.link2 || ''}>
                      {g.link2 ? <a href={g.link2} target="_blank" rel="noopener noreferrer" className="text-[#10B981] hover:underline inline-flex items-center gap-1"><LinkSimple size={12} weight="bold" />{g.link2}</a> : <span className="text-[#A1A1AA]">—</span>}
                    </TableCell>
                    <TableCell className="text-right text-sm font-mono text-[#52525B]">{g.service_count || 0}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(g)} className="h-7 w-7 p-0" data-testid={`svc-group-edit-btn-${g.id}`}><PencilSimple size={14} /></Button>
                        {!g.in_use && <Button size="sm" variant="ghost" onClick={() => del(g)} className="h-7 w-7 p-0 text-[#EF4444]" data-testid={`svc-group-delete-btn-${g.id}`}><Trash size={14} /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showDialog} onOpenChange={(v) => { if (!v) { setShowDialog(false); setConflict(null); } }}>
        <DialogContent className="max-w-md" data-testid="svc-group-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit Group: ${editing.name}` : 'New Service Group'}</DialogTitle>
            <DialogDescription className="text-xs">Links here are inherited by every member service.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. eDistrict" className="rounded-lg mt-1" data-testid="svc-group-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Link 1 (Invoice page shortcut)</Label>
              <Input value={form.link1} onChange={e => setForm(f => ({ ...f, link1: e.target.value }))} placeholder="https://portal.example.gov.in/login" className="rounded-lg mt-1" data-testid="svc-group-link1-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Link 2 (Follow-up page shortcut)</Label>
              <Input value={form.link2} onChange={e => setForm(f => ({ ...f, link2: e.target.value }))} placeholder="https://portal.example.gov.in/status" className="rounded-lg mt-1" data-testid="svc-group-link2-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={() => save('dry_run')} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] rounded-lg" data-testid="svc-group-save-btn">
              {editing ? 'Save Changes' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cascade conflict prompt */}
      <Dialog open={!!conflict} onOpenChange={(v) => { if (!v) setConflict(null); }}>
        <DialogContent className="max-w-lg" data-testid="svc-group-cascade-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Warning size={18} className="text-[#F59E0B]" /> Cascade Update?</DialogTitle>
            <DialogDescription className="text-xs">
              {conflict?.services?.length} service(s) in this group have custom links that differ from the group&apos;s.
              Choose how to handle them.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[300px] overflow-auto border border-[#E4E4E7] rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Service</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Custom Link 1</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(conflict?.services || []).map(s => (
                  <TableRow key={s.id} className="border-[#F4F4F5]">
                    <TableCell className="text-xs font-medium">{s.name}</TableCell>
                    <TableCell className="text-[10px] text-[#52525B] max-w-[280px] truncate" title={s.link1 || ''}>{s.link1 || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflict(null)} className="rounded-lg">Cancel</Button>
            <Button variant="outline" onClick={() => save('skip')} className="rounded-lg border-[#F59E0B] text-[#F59E0B] hover:bg-[#FFFBEB]" data-testid="svc-group-cascade-skip-btn">
              Skip custom — keep them as-is
            </Button>
            <Button onClick={() => save('overwrite')} className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg" data-testid="svc-group-cascade-overwrite-btn">
              Overwrite all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
